import { type JSONContent, generateHTML } from "@tiptap/core";
import MarkdownIt from "markdown-it";
import type { z } from "zod";
import { logError, logInfo, summarizeZodIssues } from "@/lib/logging";
import { ERROR_IDS } from "@/lib/constants/errorIds";
import { notesBaseExtensions, type NotesDocumentJson } from "./tiptap/extensions";
import {
  cloneJson,
  collapseAdjacentTaskLists,
  findTopLevelBlocksWithoutBlockId,
  normalizeTopLevelBlockWithMint,
  validateNoteLinkHref,
} from "./tiptap/sanitize";
import { notesDocumentSchema } from "./tiptap/schemas";

export { type NotesDocumentJson } from "./tiptap/extensions";

const markdown = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: false,
});
type MarkdownToken = ReturnType<MarkdownIt["parse"]>[number];
type NoteMark = NonNullable<JSONContent["marks"]>[number];

function cloneContentArray(content: JSONContent[] | undefined): JSONContent[] {
  return (content ?? []).map((node) => cloneJson(node));
}

function stripTaskMarkerFromParagraph(node: JSONContent): { checked: boolean; paragraph: JSONContent } | null {
  if (node.type !== "paragraph" || !node.content?.length) return null;

  const [first, ...rest] = node.content;
  if (first?.type !== "text" || typeof first.text !== "string") {
    return null;
  }

  const match = first.text.match(/^\[( |x|X)\]\s*/);
  if (!match) return null;

  const remainingText = first.text.slice(match[0].length);
  const nextContent = [
    ...(remainingText.length > 0 ? [{ ...first, text: remainingText }] : []),
    ...rest.map((child) => cloneJson(child)),
  ];

  return {
    checked: match[1].toLowerCase() === "x",
    paragraph: {
      ...cloneJson(node),
      // A bare "- [ ]" leaves nothing after the marker; ProseMirror allows
      // empty paragraphs but forbids empty text nodes (generateHTML throws).
      content: nextContent,
    },
  };
}

function normalizeTaskListItems(items: JSONContent[]): JSONContent[] | null {
  const convertedItems = items.map((item) => {
    if (item.type !== "listItem") return null;

    const childContent = cloneContentArray(item.content);
    const firstParagraphIndex = childContent.findIndex((child) => child.type === "paragraph");
    if (firstParagraphIndex < 0) return null;

    const convertedParagraph = stripTaskMarkerFromParagraph(childContent[firstParagraphIndex]);
    if (!convertedParagraph) return null;

    childContent[firstParagraphIndex] = convertedParagraph.paragraph;

    return {
      type: "taskItem",
      attrs: { checked: convertedParagraph.checked },
      content: childContent,
    } satisfies JSONContent;
  });

  if (convertedItems.some((item) => item === null)) {
    return null;
  }

  return convertedItems as JSONContent[];
}

function convertBulletListsToTaskLists(node: JSONContent): JSONContent {
  const normalizedChildren = cloneContentArray(node.content).map(convertBulletListsToTaskLists);
  const nextNode: JSONContent = normalizedChildren.length > 0 ? { ...cloneJson(node), content: normalizedChildren } : cloneJson(node);

  if (nextNode.type !== "bulletList") {
    return nextNode;
  }

  const taskItems = normalizeTaskListItems(normalizedChildren);
  if (!taskItems) {
    return nextNode;
  }

  return {
    type: "taskList",
    content: taskItems,
  };
}

function buildTextNode(text: string, marks: NonNullable<JSONContent["marks"]>): JSONContent | null {
  if (!text) return null;

  return {
    type: "text",
    text,
    ...(marks.length > 0 ? { marks: cloneJson(marks) } : {}),
  };
}

function inlineTokenToMark(token: MarkdownToken): NoteMark | null {
  switch (token.type) {
    case "em_open":
      return { type: "italic" };
    case "strong_open":
      return { type: "bold" };
    case "s_open":
      return { type: "strike" };
    case "link_open": {
      const href = typeof token.attrGet === "function" ? token.attrGet("href") ?? "" : "";
      const result = validateNoteLinkHref(href);
      return result.ok ? { type: "link", attrs: { href: result.href } } : null;
    }
    default:
      return null;
  }
}

function parseInlineTokens(tokens: MarkdownToken[] | null | undefined): JSONContent[] {
  if (!tokens?.length) return [];

  const content: JSONContent[] = [];
  const marks: NonNullable<JSONContent["marks"]> = [];

  for (const token of tokens) {
    switch (token.type) {
      case "text": {
        const node = buildTextNode(token.content, marks);
        if (node) content.push(node);
        break;
      }
      case "code_inline": {
        const node = buildTextNode(token.content, [...marks, { type: "code" }]);
        if (node) content.push(node);
        break;
      }
      case "hardbreak":
        content.push({ type: "hardBreak" });
        break;
      case "softbreak": {
        const node = buildTextNode(" ", marks);
        if (node) content.push(node);
        break;
      }
      case "em_open":
      case "strong_open":
      case "s_open":
      case "link_open": {
        const mark = inlineTokenToMark(token);
        if (mark) marks.push(mark);
        break;
      }
      case "em_close":
      case "strong_close":
      case "s_close":
      case "link_close":
        marks.pop();
        break;
      default:
        break;
    }
  }

  return content;
}

function parseMarkdownBlocks(
  tokens: MarkdownToken[],
  startIndex = 0,
  closeType?: string
): { nodes: JSONContent[]; nextIndex: number } {
  const nodes: JSONContent[] = [];
  let index = startIndex;

  while (index < tokens.length) {
    const token = tokens[index];
    if (closeType && token.type === closeType) {
      return { nodes, nextIndex: index + 1 };
    }

    switch (token.type) {
      case "heading_open": {
        const inline = tokens[index + 1];
        const level = Number(token.tag.replace("h", "")) || 1;
        nodes.push({
          type: "heading",
          attrs: { level: Math.max(1, Math.min(4, level)) },
          content: parseInlineTokens(inline?.children),
        });
        index += 3;
        break;
      }
      case "paragraph_open": {
        const inline = tokens[index + 1];
        nodes.push({
          type: "paragraph",
          content: parseInlineTokens(inline?.children),
        });
        index += 3;
        break;
      }
      case "bullet_list_open": {
        const parsed = parseMarkdownList(tokens, index, "bullet_list_close", "bulletList");
        nodes.push(parsed.node);
        index = parsed.nextIndex;
        break;
      }
      case "ordered_list_open": {
        const parsed = parseMarkdownList(tokens, index, "ordered_list_close", "orderedList");
        nodes.push(parsed.node);
        index = parsed.nextIndex;
        break;
      }
      case "blockquote_open": {
        const parsed = parseMarkdownBlocks(tokens, index + 1, "blockquote_close");
        nodes.push({
          type: "blockquote",
          content: parsed.nodes,
        });
        index = parsed.nextIndex;
        break;
      }
      case "fence": {
        // A fence holding only blank lines strips to "" — emit no text node
        // (ProseMirror forbids empty ones; the schema rejects the whole doc).
        const codeText = token.content.replace(/\n$/, "");
        nodes.push({
          type: "codeBlock",
          attrs: { language: token.info?.trim() || null },
          content: codeText ? [{ type: "text", text: codeText }] : [],
        });
        index += 1;
        break;
      }
      case "hr":
        nodes.push({ type: "horizontalRule" });
        index += 1;
        break;
      default:
        index += 1;
        break;
    }
  }

  return { nodes, nextIndex: index };
}

function parseMarkdownList(
  tokens: MarkdownToken[],
  startIndex: number,
  closeType: string,
  listType: "bulletList" | "orderedList"
): { node: JSONContent; nextIndex: number } {
  const items: JSONContent[] = [];
  // markdown-it stamps `start` on ordered_list_open only when the list does
  // not begin at 1 (TipTap's OrderedList shares that default). The integer
  // guard keeps a malformed value from becoming NaN attrs that would fail
  // the document schema.
  const start = listType === "orderedList" ? Number(tokens[startIndex].attrGet("start") ?? 1) : 1;
  const listAttrs = Number.isInteger(start) && start !== 1 ? { start } : undefined;
  const buildNode = (): JSONContent => ({
    type: listType,
    ...(listAttrs ? { attrs: listAttrs } : {}),
    content: items,
  });
  let index = startIndex + 1;

  while (index < tokens.length) {
    const token = tokens[index];
    if (token.type === closeType) {
      return { node: buildNode(), nextIndex: index + 1 };
    }

    if (token.type === "list_item_open") {
      const parsed = parseMarkdownBlocks(tokens, index + 1, "list_item_close");
      items.push({
        type: "listItem",
        content: parsed.nodes,
      });
      index = parsed.nextIndex;
      continue;
    }

    index += 1;
  }

  return { node: buildNode(), nextIndex: index };
}

/**
 * Result of attempting to parse an untrusted Tiptap document.
 * Mirrors Zod's `SafeParseResult` shape so callers can use the same
 * `.success` discriminant pattern that's idiomatic elsewhere in the codebase.
 */
export type ParseNotesDocumentResult =
  | { readonly success: true; readonly doc: NotesDocumentJson }
  | { readonly success: false; readonly error: z.ZodError };

/**
 * Shared schema+prelude for the parse/normalize paths below. Collapses the
 * zod parse, content-array coercion, and missing-blockId count into a
 * single traversal.
 */
function schemaParseNotesDocument(doc: unknown):
  | { readonly success: true; readonly content: JSONContent[]; readonly missingBlockIdCount: number }
  | { readonly success: false; readonly error: z.ZodError }
{
  const parsed = notesDocumentSchema.safeParse(doc);
  if (!parsed.success) return { success: false, error: parsed.error };
  const content = Array.isArray(parsed.data.content) ? parsed.data.content : [];
  return { success: true, content, missingBlockIdCount: findTopLevelBlocksWithoutBlockId(content) };
}

/**
 * Strict parse for untrusted notes documents. Callers that need to know
 * whether the input was valid (e.g. the export block pipeline) inspect the
 * result; `normalizeNotesDocument` wraps this for tolerant read paths.
 *
 * This variant REPAIRS: any top-level block missing a `blockId` gets a
 * freshly minted one (via `createBlockId`). That mint is observable — when
 * it happens, we log at `info` level with `NOTES_DOCUMENT_MALFORMED` so we
 * can measure how often AI outputs / markdown imports arrive without
 * blockIds. Editor-sourced content always has them (see `BlockIdExtension`),
 * so a non-zero rate here points at a non-editor entrypoint that should be
 * tightened.
 */
export function parseNotesDocument(doc: unknown): ParseNotesDocumentResult {
  const parsed = schemaParseNotesDocument(doc);
  if (!parsed.success) return parsed;
  if (parsed.missingBlockIdCount > 0) {
    logInfo(
      "[tiptap-document] parseNotesDocument minted blockIds for top-level blocks",
      {
        errorId: ERROR_IDS.NOTES_DOCUMENT_MALFORMED,
        mintedBlockIdCount: parsed.missingBlockIdCount,
        blockCount: parsed.content.length,
      }
    );
  }
  const normalizedContent = collapseAdjacentTaskLists(parsed.content.map(normalizeTopLevelBlockWithMint));
  return {
    success: true,
    doc: { type: "doc", content: normalizedContent } as NotesDocumentJson,
  };
}

/**
 * Tolerant parse for read paths — rendering AI-generated or markdown-
 * imported docs whose shape may drift from the schema, or re-rendering
 * cached editor state. On schema failure, logs at error level with
 * `NOTES_DOCUMENT_MALFORMED` so the incident surfaces in monitoring, then
 * returns an empty doc so the read path stays resilient against pre-existing
 * corruption.
 */
export function normalizeNotesDocument(doc: unknown): NotesDocumentJson {
  const result = parseNotesDocument(doc);
  if (result.success) return result.doc;
  logError(
    new Error("Notes document failed schema validation"),
    "[tiptap-document] normalizeNotesDocument received invalid document — falling back to empty doc",
    {
      errorId: ERROR_IDS.NOTES_DOCUMENT_MALFORMED,
      zodErrors: summarizeZodIssues(result.error),
      inputType: typeof doc,
    }
  );
  return { type: "doc", content: [] } as unknown as NotesDocumentJson;
}

/** Markdown → validated notes document (production path: one-shot notes generation). */
export function markdownToNotesDocument(markdownSource: string): NotesDocumentJson {
  const tokens = markdown.parse(markdownSource, {});
  const parsed = parseMarkdownBlocks(tokens).nodes;
  return normalizeNotesDocument(
    convertBulletListsToTaskLists({
      type: "doc",
      content: parsed,
    })
  );
}

export function notesDocumentToHtml(doc: unknown): string {
  return generateHTML(normalizeNotesDocument(doc), notesBaseExtensions);
}
